import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { selectEmployees, fetchAllEmployees } from 'src/stores/employeeSlice';
import ApiClient from '../ApiClient';
import { notify } from "../utils/ToastUtils";
import { ChevronLeft, CircleAlert, Key, Trash2, User, KeyRound } from 'lucide-react';
import { TextField, InputAdornment, MenuItem, Autocomplete } from '@mui/material';

const ResetPasswordForm = ({ setCurrentView }) => {
    const dispatch = useDispatch();
    const employees = useSelector(selectEmployees);

    const initialState = {
        empCode: "",
        password: ""
    };

    const [state, setState] = useState(initialState);
    const [isDisabled, setIsDisabled] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const handleChange = (evt) => {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value
        });

        if (evt.target.name === "password") {
            const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})");
            if (strongRegex.test(value)) {
                setPasswordError('Valid');
            } else {
                setPasswordError('Needs 8 chars, 1 uppercase, 1 number, 1 special');
            }
        }
    };

    const resetPassword = async () => {
        setSubmitAttempted(true);
        let currentPasswordError = "Valid";
        if (state.password) {
            const strongRegex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})");
            currentPasswordError = strongRegex.test(state.password) ? 'Valid' : 'Needs 8 chars, 1 uppercase, 1 number, 1 special';
            setPasswordError(currentPasswordError);
        }

        if (state.empCode && state.password && currentPasswordError === "Valid") {
            setIsDisabled(true);
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/resetPassword`, {
                    empcode: state.empCode,
                    password: state.password
                });
                notify.success("Password updated successfully");
                dispatch(fetchAllEmployees());
                setState(initialState);
                setPasswordError('');
                setIsDisabled(false);
                setSubmitAttempted(false);
            } catch (error) {
                console.log(error);
                setIsDisabled(false);
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
                notify.error(errmsg);
            }
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 xl:p-6 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4 border-b border-slate-100 pb-3 shrink-0">
                <div className="flex items-center gap-3">
                    <button type="button" onClick={() => { setState(initialState); setSubmitAttempted(false); setCurrentView('main'); }} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors -ml-2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                    </button>
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-100">
                        <Key size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Reset Password</h2>
                        <p className="text-xs text-slate-500 mt-0.5">Update employee credentials</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" className="px-5 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm" onClick={() => { setState(initialState); setSubmitAttempted(false); }}>
                        <Trash2 size={16} /> Discard
                    </button>
                    <button type="button" className={`px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm flex items-center gap-2 transition-colors shadow-sm ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={resetPassword} disabled={isDisabled}>
                        {isDisabled ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : "Save Changes"}
                    </button>
                </div>
            </div>

            <div className="space-y-6">
                {submitAttempted && (!state.empCode || !state.password || (passwordError !== "Valid" && passwordError !== "")) && (
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600">
                        <CircleAlert size={16} />
                        <span>Please complete all required fields and ensure the password is valid.</span>
                    </div>
                )}
                <div>
                    <h3 className="text-blue-600 font-semibold text-sm mb-3">1. Select Employee & Reset Password</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Employee Name <span className="text-red-500">*</span></label>
                            <Autocomplete
                                options={employees?.filter(el => el.STATUS === "Active") || []}
                                getOptionLabel={(option) => `${option.EMP_NAME} - ${option.EMP_ID}`}
                                value={employees?.find(el => el.EMP_CODE === state.empCode) || null}
                                onChange={(event, newValue) => {
                                    handleChange({ target: { name: 'empCode', value: newValue ? newValue.EMP_CODE : '' } });
                                }}
                                size="small"
                                className="w-full bg-white"
                                sx={{ '& .MuiOutlinedInput-root': { padding: '2px 8px', minHeight: '36px' }, '& .MuiAutocomplete-input': { padding: '4px !important', fontSize: '14px' } }}
                                ListboxProps={{ style: { maxHeight: 250, fontSize: '14px' } }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params}
                                        error={submitAttempted && !state.empCode}
                                        required
                                        placeholder="Search employee by name"
                                        className={submitAttempted && !state.empCode ? '[&_fieldset]:!border-red-500' : ''}
                                        InputProps={{ 
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ ml: 1 }}>
                                                        <User size={16} className={submitAttempted && !state.empCode ? "text-red-500" : "text-slate-400"}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }} 
                                    />
                                )}
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">New Password <span className="text-red-500">*</span></label>
                            <TextField error={submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== ""))} size="small" name="password" value={state.password} placeholder="Enter new password" onChange={handleChange} required className={`w-full bg-white ${submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== "")) ? '[&_fieldset]:!border-red-500' : ''}`} 
                                 sx={{ '& .MuiOutlinedInput-root': { minHeight: '38px' }, '& .MuiInputBase-input': { paddingTop: '6px', paddingBottom: '6px', fontSize: '14px' } }}
                                 InputProps={{ startAdornment: <InputAdornment position="start"><KeyRound size={16} className={submitAttempted && (!state.password || (passwordError !== "Valid" && passwordError !== "")) ? "text-red-500" : "text-slate-400"}/></InputAdornment> }} />
                            {submitAttempted && passwordError !== "Valid" && passwordError !== "" && <span className="text-xs text-red-500 mt-1 block">{passwordError}</span>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordForm;